import type { AdoptedSignature, FormPack, Values } from "@gsignatures/core";
import type { ReactNode } from "react";
import { SigInk } from "../components/SigInk";
import { mdy, stamp } from "../lib/format";
import { S } from "../strings";
import { rectStyle } from "./LetterOverlay";

export interface FormOverlayOpts {
  pack: FormPack;
  values: Values;
  /** The question being answered: its box is highlighted with its line. */
  activeField?: string | null;
  /** fill: answers + quiet hints for the signature · sign: "Sign here" · signed: the signature, date and name. */
  mode: "fill" | "sign" | "signed";
  signature?: AdoptedSignature | undefined;
  signedAt?: string | undefined;
  animate?: boolean;
}

/** The answers drawn where the official form's boxes are (the pack's layout), as the person types. */
export function formOverlay(o: FormOverlayOpts) {
  const { pack } = o;
  const boxes = pack.boxes(o.values);
  const sig = pack.signature;
  const own = new Set([sig.box, sig.dateBox, sig.printNameBox].filter((x): x is string => !!x));
  const activeBox = o.activeField ? pack.boxOf[o.activeField] : undefined;
  const activeSpec = o.activeField ? pack.fields.find((f) => f.id === o.activeField) : undefined;
  const activeLine = activeSpec?.meta?.line !== undefined ? S.tax.lineChip(String(activeSpec.meta.line)) : undefined;
  const printed = sig.printNameFrom ? String(o.values[sig.printNameFrom] ?? "").trim() : "";

  return (page: number, aspect: number): ReactNode => {
    const items: ReactNode[] = [];
    for (const [key, rect] of Object.entries(pack.layout)) {
      if (rect.page !== page || own.has(key)) continue;
      const v = boxes[key];
      const active = key === activeBox && o.mode === "fill";
      if (typeof v === "boolean") {
        if (!v && !active) continue;
        items.push(
          <div key={key} className={`ov box ${active ? "active" : ""}`} style={rectStyle(rect)} data-line={active ? activeLine : undefined} data-focus={active ? "true" : undefined}>
            {v ? "X" : ""}
          </div>,
        );
        continue;
      }
      if (!v && !active) continue;
      items.push(
        <div key={key} className={`ov ${active ? "active" : ""}`} style={rectStyle(rect)} data-line={active ? activeLine : undefined} data-focus={active ? "true" : undefined}>
          {v}
        </div>,
      );
    }

    const box = pack.layout[sig.box];
    const date = sig.dateBox ? pack.layout[sig.dateBox] : undefined;
    const name = sig.printNameBox ? pack.layout[sig.printNameBox] : undefined;
    if (box && box.page === page) {
      if (o.mode === "signed") {
        const fs = `${(box.h * aspect * 1.45).toFixed(2)}cqw`;
        items.push(
          <div key="sig" className="ov sig" style={{ ...rectStyle(box), width: `${box.w * 0.62}%` }} data-focus="true">
            <SigInk sig={o.signature} fontSize={fs} animate={o.animate} />
          </div>,
        );
        if (o.signedAt) {
          items.push(
            <div key="stamp" className="ov stamp" style={{ ...rectStyle(box), left: `${box.l + box.w * 0.64}%`, width: `${box.w * 0.36}%` }}>
              {S.sign.eSigned(stamp(o.signedAt))}
            </div>,
          );
        }
      } else {
        const signHere = o.mode === "sign";
        items.push(
          <div key="sig" className={`ov ghost ${signHere ? "active sign-here" : ""}`} style={rectStyle(box)} data-line={signHere ? S.tax.signHere : undefined} data-focus={signHere ? "true" : undefined}>
            {S.tax.ghostSignature}
          </div>,
        );
      }
    }
    if (date && date.page === page) {
      items.push(
        <div key="date" className={`ov ${o.mode === "signed" ? "" : "ghost"}`} style={rectStyle(date)}>
          {o.mode === "signed" && o.signedAt ? mdy(new Date(o.signedAt)) : S.tax.ghostDate}
        </div>,
      );
    }
    if (name && name.page === page && printed) {
      items.push(
        <div key="name" className={`ov ${o.mode === "signed" ? "" : "ghost"}`} style={rectStyle(name)}>
          {printed}
        </div>,
      );
    }
    return <>{items}</>;
  };
}
